import { useState } from "react";

import {
  Accordion,
  ButtonGroup,
  Card,
  Row,
  ToggleButton,
} from "react-bootstrap";

import { DateTime } from "luxon";

import ModalBeRepresented from "./ModalBeRepresented";
import ModalPostponed from "./ModalPostponed";
import ModalValidation from "./ModalValidation";
import {
  getServiceOrderTypeValuesByType,
  ServiceOrderType,
} from "../../../_constants/serviceOrder.ts";
import { getICSData } from "../../../_helpers/agendaHelper.ts";
import {
  dateFormatIcal,
  dateFormatWeekday,
  dateFormatWeekdayTime,
} from "../../../_helpers/dateHelper.ts";
import AgendaConf from "../../../assets/svg/Agenda_Conf";
import ChronoConf from "../../../assets/svg/chronoConf";
import LittleAgendaConf from "../../../assets/svg/littleAgendaConf";
import Signature from "../../../assets/svg/signature_conf";
import ValidationConf from "../../../assets/svg/validation_Conf";
import ColoredMarker from "../../../common/components/ColoredMarker";
import ServiceTypeComponent from "../../../common/components/ServiceTypeComponent.tsx";
import { IService } from "../../serviceOrdering/reducers";
import { IAvailabilities, IConfirmationAppointment } from "../reducers";
import { updateAppointment } from "../services";

interface SelectAndConfirmationFormProps {
  appointment: IConfirmationAppointment;
  availabilities: IAvailabilities;
  service: IService;
}

const SelectAndConfirmationForm = ({
  availabilities,
  appointment,
  service,
}: SelectAndConfirmationFormProps) => {
  const [representedModal, setRepresentedModal] = useState(false);
  const [postponedModal, setPostponedModal] = useState(false);
  const [validationModal, setValidationModal] = useState(false);
  const [isValidate, setIsValidate] = useState(false);
  const [isPostponed, setIsPostponed] = useState(false);
  const serviceType = getServiceOrderTypeValuesByType(
    service?.type as keyof object,
  )?.label;
  
  // Safely parse appointment dates
  const [appointmentDate, setAppointmentDate] = useState(() => {
    try {
      return DateTime.fromISO(appointment.startDate);
    } catch (e) {
      console.warn("Invalid appointment start date, using current date", e);
      return DateTime.now();
    }
  });
  
  const [appointmentHour, setAppointmentHour] = useState(() => {
    try {
      return DateTime.fromISO(appointment.startDate).hour;
    } catch (e) {
      console.warn("Invalid appointment hour, using default", e);
      return 9; // Default to 9 AM if there's an error
    }
  });

  const appointmentMinute = (() => {
    try {
      const minute = DateTime.fromISO(appointment.startDate).minute;
      return minute !== undefined && minute !== null ? minute : 0;
    } catch (e) {
      console.warn("Error parsing appointment minute", e);
      return 0;
    }
  })();
  
  const isChecked = (hour: number) => appointmentHour === hour;

  const label = () => {
    switch (service.type) {
      case ServiceOrderType.INCOMING_INVENTORY:
        return "Prendre rendez-vous plus tard ?";
      case ServiceOrderType.OUTGOING_INVENTORY:
        return "Prendre rendez-vous plus tôt ?";
      case ServiceOrderType.OUTGOING_INCOMING_INVENTORY:
        return "Choisir une autre date";
    }
  };

  const confirmAppointment = function () {
    try {
      const body = {
        startDate: appointmentDate.set({ hour: appointmentHour }).toISO(),
        endDate: appointmentDate.set({ hour: appointmentHour }).toISO(),
      };
      updateAppointment(appointment.id, body).then();
    } catch (e) {
      console.error("Error creating appointment dates:", e);
    }
  };

  const getLeaseStartDate = () => {
    try {
      for (const person of appointment.people) {
        if (person?.lastTenant?.leaseStartDate) {
          return DateTime.fromISO(person.lastTenant.leaseStartDate).toFormat(
            dateFormatWeekday,
          );
        }
      }
      return "N/A";
    } catch (e) {
      console.warn("Error getting lease start date:", e);
      return "N/A";
    }
  };

  const getLeaseEndDate = () => {
    try {
      for (const person of appointment.people) {
        if (person?.lastTenant?.leaseEndDate) {
          return DateTime.fromISO(person.lastTenant.leaseEndDate).toFormat(
            dateFormatWeekday,
          );
        }
      }
      return "N/A";
    } catch (e) {
      console.warn("Error getting lease end date:", e);
      return "N/A";
    }
  };

  const addToAgenda = () => {
    try {
      // Safely format date strings for calendar
      const startDateStr = appointment?.startDate ? 
        DateTime.fromISO(appointment.startDate).toFormat(dateFormatIcal) : 
        DateTime.now().toFormat(dateFormatIcal);
      
      const endDateStr = appointment?.endDate ? 
        DateTime.fromISO(appointment.endDate).toFormat(dateFormatIcal) : 
        DateTime.now().plus({ hours: 1 }).toFormat(dateFormatIcal);
      
      const icsContent = getICSData(
        `${serviceType || "Rendez-vous"}`,
        "",
        service?.dwelling?.addressLabel || "Adresse non spécifiée",
        startDateStr,
        endDateStr
      );
      
      window.open(
        `data:text/calendar;charset=utf8,${encodeURIComponent(icsContent)}`,
      );
    } catch (e) {
      console.error("Error generating calendar data:", e);
    }
  };

  return (
    <>
      {!isValidate && (
        <>
          <Accordion defaultActiveKey="0">
            <Accordion.Item eventKey="0">
              <Accordion.Header>
                <AgendaConf className="me-4" />
                <div>Prenez rendez-vous</div>
              </Accordion.Header>
              <Accordion.Body>
                <div className="d-flex justify-content-between mt-1">
                  <ServiceTypeComponent
                    type={service?.type as ServiceOrderType}
                    label={serviceType}
                  />
                  <div className="addressConf">Durée: 1h</div>
                </div>

                <br />
                <div className="addressConf">
                  {service?.dwelling?.addressLabel}
                </div>
                <div className="d-flex align-items-center mt-1 flex-wrap">
                  {!service?.dwelling?.category ?? (
                    <ColoredMarker className="me-2 mb-2" color="#641ABF">
                      {service?.dwelling?.category?.label}
                    </ColoredMarker>
                  )}
                  {(!service?.dwelling?.type &&
                    !service?.dwelling?.type?.label) ?? (
                    <ColoredMarker className="me-2 mb-2" color="#641ABF">
                      {service?.dwelling?.type?.label}
                    </ColoredMarker>
                  )}
                  {(!service?.dwelling?.condition &&
                    !service?.dwelling?.condition?.label) ?? (
                    <ColoredMarker className="me-2 mb-2" color="#641ABF">
                      {service?.dwelling?.condition?.label}
                    </ColoredMarker>
                  )}
                  {!service?.dwelling?.area ?? (
                    <ColoredMarker className="me-2 mb-2" color="#641ABF">
                      {service?.dwelling?.area} m²
                    </ColoredMarker>
                  )}
                  {!service?.dwelling?.address?.floor ?? (
                    <ColoredMarker className="me-2 mb-2" color="#641ABF">
                      {service?.dwelling?.address?.floor}
                    </ColoredMarker>
                  )}
                  {!service?.dwelling?.managementLotNumber ?? (
                    <ColoredMarker className="me-2 mb-2" color="#641ABF">
                      N° lot: {service?.dwelling?.managementLotNumber}
                    </ColoredMarker>
                  )}
                </div>
                <div className="subTextConf mt-4">
                  Sélectionnez cotre créneau en fonction des disponibilité.
                </div>
                <div className="d-flex subTextBoldConf">
                  Nous vous recommandons de réserver rapidement votre
                  rendez-vous
                </div>
                {service?.type === "incomingInventory" ||
                service?.type === "outgoingIncomingInventory" ? (
                  <div className="d-flex align-items-center mt-4 flex-wrap addressConf">
                    <Signature />
                    <div className="ms-1">Signature du bail le :&nbsp;</div>
                    <div className="subTitleBoldConf">
                      {getLeaseStartDate()}
                    </div>
                  </div>
                ) : (
                  <></>
                )}

                {service?.type === "outgoingInventory" ||
                service?.type === "outgoingIncomingInventory" ? (
                  <div className="d-flex align-items-center mt-4 flex-wrap addressConf">
                    <Signature />
                    <div className="ms-1">Fin du bail le :&nbsp;</div>
                    <div className="subTitleBoldConf">{getLeaseEndDate()}</div>
                  </div>
                ) : (
                  <></>
                )}

                {!isPostponed ? (
                  <Card className="pt-auto">
                    <Card.Header className="d-flex align-items-center flex-wrap addressConf">
                      <div>{serviceType} le :&nbsp;</div>
                      <div className="subTitleBoldConf">
                        {appointmentDate
                          .set({ hour: appointmentHour })
                          .toFormat(dateFormatWeekdayTime)}
                      </div>
                    </Card.Header>
                    <Card.Body>
                      <>
                        <Row>
                          <ButtonGroup className="mt-2">
                            <div className="mx-1 d-flex flex-wrap">
                              {availabilities &&
                                Object.keys(availabilities).length > 0 &&
                                availabilities[
                                  Object.keys(availabilities)[0]
                                ].hours.map((entry: any, index: number) => {
                                  return (
                                    <ToggleButton
                                      id="availabilities"
                                      key={index}
                                      className="btn-order ps-4 pe-4 m-1"
                                      type="radio"
                                      checked={isChecked(entry)}
                                      value={`${entry}:${appointmentMinute}`}
                                      onClick={() => {
                                        setAppointmentHour(entry);
                                        setAppointmentDate(appointmentDate);
                                      }}
                                    >
                                      {`${entry}:${appointmentMinute}`}
                                    </ToggleButton>
                                  );
                                })}
                            </div>
                          </ButtonGroup>
                        </Row>
                      </>
                    </Card.Body>
                  </Card>
                ) : (
                  <>
                    <div className="p-4 mt-4 mb-4 postponedWarningConf">
                      Vous avez indiqué ne pas être disponible le&nbsp;
                      {DateTime.fromISO(appointment?.startDate).toFormat(
                        dateFormatWeekdayTime,
                      )}
                      &nbsp;pour votre&nbsp;
                      {serviceType}. Consultez d’autres disponibilités.
                    </div>
                    <Accordion defaultActiveKey={"2"}>
                      {Object.entries(availabilities).map(
                        (availability, index) => {
                          return (
                            <Accordion.Item
                              key={index}
                              eventKey={(index + 2).toString()}
                            >
                              <Accordion.Header>
                                {DateTime.fromFormat(
                                  availability[0],
                                  "M/d/y",
                                ).toFormat(dateFormatWeekday)}
                              </Accordion.Header>
                              <Accordion.Body>
                                <>
                                  <Row>
                                    <ButtonGroup className="mt-2">
                                      <div className="mx-1 d-flex flex-wrap">
                                        {availability[1]?.hours?.map(
                                          (entry: any, index: number) => {
                                            return (
                                              <ToggleButton
                                                id="postponed-availabilities"
                                                key={index}
                                                className="btn-order ps-4 pe-4 m-1"
                                                type="radio"
                                                checked={isChecked(entry)}
                                                value={`${entry}:${appointmentMinute}`}
                                                onClick={() => {
                                                  setAppointmentHour(entry);
                                                  setAppointmentDate(
                                                    DateTime.fromFormat(
                                                      availability[0],
                                                      "MM/d/yyyy",
                                                    ),
                                                  );
                                                }}
                                              >
                                                {`${entry}:${appointmentMinute}`}
                                              </ToggleButton>
                                            );
                                          },
                                        )}
                                      </div>
                                    </ButtonGroup>
                                  </Row>
                                </>
                              </Accordion.Body>
                            </Accordion.Item>
                          );
                        },
                      )}
                    </Accordion>
                  </>
                )}

                <Card className="pt-auto mt-4">
                  <Card.Body>
                    <>
                      <Row
                        onClick={() => {
                          setRepresentedModal(true);
                        }}
                      >
                        <div>Vous ne pourrez pas être sur place ?</div>
                      </Row>
                    </>
                  </Card.Body>
                </Card>
                {!isPostponed && (
                  <Card className="pt-auto mt-4">
                    <Card.Body>
                      <>
                        <Row
                          onClick={() => {
                            setPostponedModal(true);
                          }}
                        >
                          <div>{label()}</div>
                        </Row>
                      </>
                    </Card.Body>
                  </Card>
                )}

                <div className="mt-5 d-flex flex-row justify-content-center">
                  <button
                    className="pt-2 pb-2 ps-5 pe-5 BtnValConf"
                    onClick={() => {
                      setValidationModal(true);
                    }}
                  >
                    Réserver
                  </button>
                </div>
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>
        </>
      )}
      {isValidate && (
        <>
          <div className="m-4 p-4 valBlockConf d-flex flex-row">
            <ValidationConf className="me-3" />
            <div className="d-flex flex-column">
              <div className="valTextConf">Merci !</div>
              <div className="d-flex flex-wrap valSubTextConf">
                Votre rendez-vous a bien été confirmé
              </div>
            </div>
          </div>
          <div className="m-4 ps-4 pe-4 d-flex flex-row pt-3 pb-3 valHeaderConf subTitleConf justify-content-between">
            <div className="d-flex flex-row">
              <LittleAgendaConf />
              <div className="ms-4">
                {appointmentDate
                  .set({ hour: appointmentHour })
                  .toFormat(dateFormatWeekdayTime)}
              </div>
            </div>
          </div>

          <div className="mt-4 ps-4 pe-4 d-flex flex-row justify-content-between">
            <div className="flex-column d-flex">
              <div className="subTitleBoldConf">{serviceType}</div>
              <div className="addressConf">
                {service?.dwelling?.addressLabel}
              </div>
            </div>
            <div className="d-flex flex-row">
              <div className="align-self-center me-2">
                <ChronoConf />
              </div>
              <div className="align-self-center">1h</div>
            </div>
          </div>
          <div className="d-grid gap-2">
            <button className="m-4 p-2 valBtnAgendaConf" onClick={addToAgenda}>
              Ajouter à mon agenda
            </button>
          </div>
        </>
      )}
      <ModalBeRepresented
        setShow={setRepresentedModal}
        show={representedModal}
      />
      <ModalPostponed
        appointment={appointment}
        setShow={setPostponedModal}
        show={postponedModal}
        setIsPostponed={setIsPostponed}
      />
      <ModalValidation
        address={service.dwelling?.addressLabel}
        appointmentDateTime={appointment.startDate}
        confirmAppointment={confirmAppointment}
        setShow={setValidationModal}
        show={validationModal}
        setIsValidate={setIsValidate}
        service={serviceType}
      />
    </>
  );
};

export default SelectAndConfirmationForm;
